<script setup>
import { ref, computed, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';
import { openUnreadConversationList } from 'dashboard/helper/openUnreadConversationList';

import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import wootConstants from 'dashboard/constants/globals';
import ScheduleMessageModal from 'dashboard/components/widgets/conversation/bubble/ScheduleMessageModal.vue';
import {
  CMD_REOPEN_CONVERSATION,
  CMD_RESOLVE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';
import MobileConversationMenu from 'dashboard/components/widgets/conversation/mobile/MobileConversationMenu.vue';
import LeadSheetModal from 'dashboard/components-next/Conversation/LeadSheet/LeadSheetModal.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

// SiteUp: no header variação C o Resolver fica agrupado com IA ativa/Pausar.
// `grouped` remove sombra/outline do ButtonGroup pra encaixar no pill.
const props = defineProps({
  grouped: {
    type: Boolean,
    default: false,
  },
  mobileMenu: {
    type: Boolean,
    default: false,
  },
  mobileMenuItems: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['mobileMenuAction']);

const store = useStore();
const getters = useStoreGetters();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const { checkMissingAttributes } = useConversationRequiredAttributes();

const arrowDownButtonRef = ref(null);
const isLoading = ref(false);
const resolveAttributesModalRef = ref(null);
const showMobileMenu = ref(false);

const [showActionsDropdown, toggleDropdown] = useToggle();
const closeDropdown = () => toggleDropdown(false);
const openDropdown = () => {
  if (props.mobileMenu) {
    showMobileMenu.value = true;
    return;
  }
  toggleDropdown(true);
};
const closeMobileMenu = async () => {
  showMobileMenu.value = false;
  await nextTick();
  arrowDownButtonRef.value?.$el?.focus();
};

const currentChat = computed(() => getters.getSelectedChat.value);

// SiteUP CRM (Módulo Imobiliária): "Gerar ficha do lead" só existe na conta com
// o módulo ligado. Falha fechando: sem conta ou sem o getter, o item não aparece.
const isLeadSheetEnabled = computed(() => {
  const isEnabled = getters['accounts/isFeatureEnabledonAccount']?.value;
  const accountId = getters.getCurrentAccountId?.value;
  if (typeof isEnabled !== 'function' || !accountId) return false;
  return Boolean(isEnabled(accountId, FEATURE_FLAGS.REAL_ESTATE_MODULE));
});
const leadSheetRef = ref(null);

const isOpen = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.OPEN
);
const isPending = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.PENDING
);
const isResolved = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.RESOLVED
);
const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

// Na imobiliária a conversa pendente (IA atendendo) também precisa da seta:
// é por ela que o corretor chega na ficha do lead.
const showAdditionalActions = computed(
  () =>
    props.mobileMenu ||
    isLeadSheetEnabled.value ||
    (!isPending.value && !isSnoozed.value)
);

const showOpenButton = computed(() => {
  return isPending.value || isSnoozed.value;
});

const getConversationParams = () => {
  const allConversations = document.querySelectorAll(
    '.conversations-list .conversation'
  );

  const activeConversation = document.querySelector(
    'div.conversations-list div.conversation.active'
  );
  const activeConversationIndex = [...allConversations].indexOf(
    activeConversation
  );
  const lastConversationIndex = allConversations.length - 1;

  return {
    all: allConversations,
    activeIndex: activeConversationIndex,
    lastIndex: lastConversationIndex,
  };
};

// Lucas 2026-08-15: o dropdown perde o snooze e ganha não-lido + agendar
// mensagem (o snooze segue acessível pelo ⌘K se alguém sentir falta).
const showScheduleMessageModal = ref(false);
const openScheduleMessage = () => {
  closeDropdown();
  showScheduleMessageModal.value = true;
};
const openLeadSheet = () => {
  closeDropdown();
  leadSheetRef.value?.open();
};
const markAsUnread = async () => {
  closeDropdown();
  try {
    await store.dispatch('markMessagesUnread', { id: currentChat.value.id });
    openUnreadConversationList(router, {
      accountId: route.params.accountId,
      inboxId: route.params.inbox_id,
      label: route.params.label,
      teamId: route.params.teamId,
    });
  } catch {
    useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.API.MARK_AS_UNREAD_FAILED'));
  }
};

const toggleStatus = (status, snoozedUntil, customAttributes = null) => {
  closeDropdown();
  isLoading.value = true;

  const payload = {
    conversationId: currentChat.value.id,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  store.dispatch('toggleStatus', payload).then(() => {
    useAlert(t('CONVERSATION.CHANGE_STATUS'));
    isLoading.value = false;
  });
};

const handleResolveWithAttributes = ({ attributes, context }) => {
  if (context) {
    toggleStatus(
      wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      attributes
    );
  }
};

const onCmdOpenConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.OPEN);
};

const onCmdResolveConversation = () => {
  const currentCustomAttributes = currentChat.value.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );

  if (hasMissing) {
    const conversationContext = {
      id: currentChat.value.id,
      snoozedUntil: null,
    };
    resolveAttributesModalRef.value?.open(
      missing,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);
  }
};

const handleStatusAction = action => {
  if (action === 'toggle_status') {
    if (isResolved.value || isSnoozed.value) {
      onCmdOpenConversation();
    } else {
      onCmdResolveConversation();
    }
    return true;
  }
  if (action === 'mark_pending') {
    toggleStatus(wootConstants.STATUS_TYPE.PENDING);
    return true;
  }
  return false;
};

const onMobileMenuAction = item => {
  showMobileMenu.value = false;
  if (handleStatusAction(item.action)) return;
  if (item.action === 'lead_sheet') {
    openLeadSheet();
    return;
  }
  emit('mobileMenuAction', item);
};

defineExpose({ handleStatusAction, openLeadSheet });

const keyboardEvents = {
  'Alt+KeyM': {
    action: () => arrowDownButtonRef.value?.$el.click(),
    allowOnFocusedInput: true,
  },
  'Alt+KeyE': {
    action: async () => {
      onCmdResolveConversation();
    },
  },
  '$mod+Alt+KeyE': {
    action: async event => {
      const { all, activeIndex, lastIndex } = getConversationParams();
      onCmdResolveConversation();

      if (activeIndex < lastIndex) {
        all[activeIndex + 1].click();
      } else if (all.length > 1) {
        all[0].click();
        document.querySelector('.conversations-list').scrollTop = 0;
      }
      event.preventDefault();
    },
  },
};

useKeyboardEvents(keyboardEvents);

useEmitter(CMD_REOPEN_CONVERSATION, onCmdOpenConversation);
useEmitter(CMD_RESOLVE_CONVERSATION, onCmdResolveConversation);
</script>

<template>
  <div
    class="flex relative justify-end items-center resolve-actions"
    data-testid="conversation-resolve"
  >
    <!-- SiteUp V2 (G4): grouped = pílula branca ~32px (estilo Post do X) no header -->
    <ButtonGroup
      class="flex-shrink-0 outline-1 outline"
      :class="[
        props.grouped
          ? 'shadow-none outline-transparent !rounded-full gap-0'
          : 'shadow rounded-2xl',
        !props.grouped && !showOpenButton
          ? 'outline-n-container'
          : 'outline-transparent',
      ]"
    >
      <Button
        v-if="isOpen"
        :label="t('CONVERSATION.HEADER.RESOLVE_ACTION')"
        size="sm"
        :color="props.grouped ? 'slate' : 'blue'"
        no-animation
        :class="
          props.grouped
            ? '!h-8 !min-h-8 !px-3.5 !text-[12.5px] !font-bold !rounded-full !bg-n-brand !text-white hover:enabled:!bg-n-blue-10 !outline-0 ltr:!rounded-r-none rtl:!rounded-l-none'
            : 'ltr:rounded-r-none rtl:rounded-l-none !outline-0'
        "
        :is-loading="isLoading"
        @click="onCmdResolveConversation"
      />
      <Button
        v-else-if="isResolved"
        :label="t('CONVERSATION.HEADER.REOPEN_ACTION')"
        size="sm"
        :color="props.grouped ? 'slate' : 'blue'"
        no-animation
        :class="
          props.grouped
            ? '!h-8 !min-h-8 !px-3.5 !text-[12.5px] !font-bold !rounded-full !bg-n-brand !text-white hover:enabled:!bg-n-blue-10 !outline-0 ltr:!rounded-r-none rtl:!rounded-l-none'
            : 'ltr:rounded-r-none rtl:rounded-l-none !outline-0'
        "
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-else-if="showOpenButton"
        :label="t('CONVERSATION.HEADER.OPEN_ACTION')"
        size="sm"
        color="slate"
        no-animation
        :class="
          props.grouped
            ? [
                '!h-8 !min-h-8 !px-3.5 !text-[12.5px] !font-bold !rounded-full !bg-n-brand !text-white hover:enabled:!bg-n-blue-10 !outline-0',
                showAdditionalActions
                  ? 'ltr:!rounded-r-none rtl:!rounded-l-none'
                  : '',
              ]
            : ''
        "
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-if="showAdditionalActions"
        ref="arrowDownButtonRef"
        icon="i-ri-arrow-down-s-line"
        :aria-label="t('CONVERSATION.HEADER.MORE_ACTIONS')"
        :aria-expanded="props.mobileMenu ? showMobileMenu : showActionsDropdown"
        :aria-haspopup="props.mobileMenu ? 'dialog' : 'menu'"
        :disabled="isLoading"
        size="sm"
        no-animation
        class="ltr:rounded-l-none rtl:rounded-r-none !outline-0"
        :class="
          props.grouped
            ? '!h-8 !min-h-8 !px-2 !rounded-full !bg-n-brand !text-white hover:enabled:!bg-n-blue-10 ltr:!rounded-l-none rtl:!rounded-r-none'
            : ''
        "
        :color="
          props.grouped ? 'slate' : isOpen || isResolved ? 'blue' : 'slate'
        "
        trailing-icon
        @click="openDropdown"
      />
    </ButtonGroup>
    <div
      v-if="showActionsDropdown && !props.mobileMenu"
      v-on-clickaway="closeDropdown"
      class="border rounded-2xl outline outline-1 outline-n-weak border-n-strong dark:border-n-strong box-content p-2 w-fit z-10 bg-n-alpha-3 backdrop-blur-[100px] absolute block left-auto top-full mt-0.5 start-0 xl:start-auto xl:end-0 min-w-[9.75rem] [&_ul>li]:mb-0"
      :class="isLeadSheetEnabled ? 'max-w-[16.25rem]' : 'max-w-[12.5rem]'"
    >
      <WootDropdownMenu class="mb-0">
        <WootDropdownItem>
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.MARK_UNREAD')"
            ghost
            slate
            sm
            start
            icon="i-ri-mail-unread-line"
            class="w-full"
            @click="markAsUnread"
          />
        </WootDropdownItem>
        <WootDropdownItem>
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.SCHEDULE_MESSAGE')"
            ghost
            slate
            sm
            start
            icon="i-ri-chat-history-line"
            class="w-full"
            @click="openScheduleMessage"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="!isPending">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.MARK_PENDING')"
            ghost
            slate
            sm
            start
            icon="i-ri-radio-button-line"
            class="w-full"
            @click="() => toggleStatus(wootConstants.STATUS_TYPE.PENDING)"
          />
        </WootDropdownItem>
        <template v-if="isLeadSheetEnabled">
          <li class="my-1.5 mx-1 h-px bg-n-weak" role="separator" />
          <WootDropdownItem>
            <button
              type="button"
              class="flex w-full items-center gap-2 rounded-lg px-2 h-8 text-sm font-semibold text-n-slate-12 hover:bg-n-alpha-2 whitespace-nowrap"
              data-testid="resolve-lead-sheet"
              @click="openLeadSheet"
            >
              <span class="i-ph-clipboard-text size-4 shrink-0" />
              {{ t('CONVERSATION.LEAD_SHEET.MENU_ITEM') }}
              <span class="ms-auto ps-2 text-[11px] font-bold text-n-violet-11">
                {{ t('CONVERSATION.LEAD_SHEET.AI_TAG') }}
              </span>
            </button>
          </WootDropdownItem>
        </template>
      </WootDropdownMenu>
    </div>
    <ScheduleMessageModal
      v-if="showScheduleMessageModal && currentChat?.id"
      :show="showScheduleMessageModal"
      :conversation-id="currentChat.display_id ?? currentChat.id"
      @close="showScheduleMessageModal = false"
    />
    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />
    <MobileConversationMenu
      v-if="showMobileMenu"
      :items="props.mobileMenuItems"
      @action="onMobileMenuAction"
      @close="closeMobileMenu"
    />
    <LeadSheetModal
      v-if="isLeadSheetEnabled && currentChat?.id"
      ref="leadSheetRef"
      :conversation-id="currentChat.display_id ?? currentChat.id"
    />
  </div>
</template>
